<template>
    <div class="profile-page">
        <PageTitleBanner :title="$t('m.profileTitle')" showBack/>

        <div class="profile-page-body">
            <div class="profile-content-card">
                <ProfileSkeleton v-if="isProfileLoading" />
                <div v-else class="profile-form">
                    <div class="r-i-name-group">
                        <div class="profile-field">
                            <div class="profile-field-label">{{ $t('m.fNameInput') }}</div>
                            <input
                                v-model.trim="formData.first_name"
                                class="r-i-input"
                                type="text"
                                maxlength="50"
                                @input="handleFieldInput"
                            >
                        </div>
                        <div class="profile-field">
                            <div class="profile-field-label">{{ $t('m.lNameInput') }}</div>
                            <input
                                v-model.trim="formData.last_name"
                                class="r-i-input"
                                type="text"
                                maxlength="50"
                                @input="handleFieldInput"
                            >
                        </div>
                    </div>

                    <div class="profile-field">
                        <div class="profile-field-label">{{ $t('m.emailInput') }}</div>
                        <input
                            v-model.trim="formData.email"
                            class="r-i-input"
                            :class="{ readonly: emailReadonly }"
                            type="email"
                            maxlength="254"
                            :readonly="emailReadonly"
                            @input="handleFieldInput"
                        >
                    </div>

                    <div class="profile-field">
                        <div class="profile-field-label">{{ $t('m.phonePlaceholder') }}</div>
                        <input
                            v-model.trim="formData.tel"
                            class="r-i-input"
                            :class="{ readonly: telReadonly }"
                            type="tel"
                            inputmode="numeric"
                            maxlength="20"
                            :readonly="telReadonly"
                            @input="handleFieldInput"
                        >
                    </div>

                    <div class="profile-field">
                        <div class="profile-field-label">{{ $t('m.postCodeInput') }}</div>
                        <input
                            v-model.trim="formData.post_code"
                            class="r-i-input"
                            type="text"
                            maxlength="20"
                            @input="handleFieldInput"
                        >
                    </div>

                    <button
                        v-active-touch
                        class="primary-btn profile-save-btn"
                        :class="{ invalid: !hasChanges }"
                        type="button"
                        @click="submitProfile"
                    >
                        {{ $t('m.profileSave') }}
                    </button>
                </div>
            </div>

            <button
                v-if="!isProfileLoading"
                v-active-touch
                class="profile-delete-btn interactive-text"
                type="button"
                @click="isDeleteConfirmVisible = true"
            >
                {{ $t('m.profileDeleteAccount') }}
            </button>
        </div>

        <PopupConfirm
            class="profile-delete-popup"
            :title="$t('m.profileDeleteConfirmTitle')"
            :showPopup="isDeleteConfirmVisible"
            :body-scrollable="false"
            @hidePopup="isDeleteConfirmVisible = false"
        >
            <div class="profile-delete-confirm">
                <div class="profile-delete-confirm-content">{{ $t('m.profileDeleteConfirmContent') }}</div>
                <button
                    v-active-touch
                    class="primary-btn profile-delete-confirm-btn"
                    type="button"
                    @click="confirmDeleteAccount"
                >
                    {{ $t('m.profileDeleteAccount') }}
                </button>
            </div>
        </PopupConfirm>
    </div>
</template>

<script>
import { deleteAccount, editProfile, getUserInfo } from "../../api/logined";
import PageTitleBanner from "../../components/PageTitleBanner.vue";
import PopupConfirm from "../../components/popups/PopupConfirm.vue";
import ProfileSkeleton from "./components/ProfileSkeleton.vue";

const EMAIL_RE = /[a-z0-9]+([._\\-]*[a-z0-9])*@([a-z0-9]+[-a-z0-9]*[a-z0-9]+.){1,63}[a-z0-9]+/;
const TEL_RE = /^\d{6,20}$/;

function normalizeText(value) {
    return String(value || "").trim();
}

export default {
    name: "Profile",
    components: {
        PageTitleBanner,
        PopupConfirm,
        ProfileSkeleton,
    },
    data() {
        return {
            formData: {
                first_name: "",
                last_name: "",
                email: "",
                tel: "",
                post_code: "",
            },
            originalFormData: {
                first_name: "",
                last_name: "",
                email: "",
                tel: "",
                post_code: "",
            },
            emailReadonly: false,
            telReadonly: false,
            isDeleteConfirmVisible: false,
            isProfileLoading: true,
        };
    },
    computed: {
        hasChanges() {
            return (
                normalizeText(this.formData.first_name) !== normalizeText(this.originalFormData.first_name) ||
                normalizeText(this.formData.last_name) !== normalizeText(this.originalFormData.last_name) ||
                normalizeText(this.formData.email) !== normalizeText(this.originalFormData.email) ||
                normalizeText(this.formData.tel) !== normalizeText(this.originalFormData.tel) ||
                normalizeText(this.formData.post_code) !== normalizeText(this.originalFormData.post_code)
            );
        },
    },
    created() {
        this.loadProfile();
    },
    methods: {
        async loadProfile() {
            this.isProfileLoading = true;
            try {
                const res = await getUserInfo();
                const profile = (res && res.data) || {};
                const primaryEmail = normalizeText(profile.email);
                const extendEmail = normalizeText(profile.extend_email);
                const primaryTel = normalizeText(profile.tel);
                const extendTel = normalizeText(profile.extend_tel);

                this.emailReadonly = !!primaryEmail;
                this.telReadonly = !!primaryTel;

                this.formData = {
                    first_name: normalizeText(profile.first_name),
                    last_name: normalizeText(profile.last_name),
                    email: primaryEmail || extendEmail,
                    tel: primaryTel || extendTel,
                    post_code: normalizeText(profile.post_code),
                };
                this.originalFormData = { ...this.formData };
            } catch (error) {
                this.toast(error && error.message);
            } finally {
                this.isProfileLoading = false;
            }
        },
        handleFieldInput() {},
        validateForm() {
            if (this.formData.email && !EMAIL_RE.test(this.formData.email)) {
                this.toast(this.$t("tMsg.wrongEmail"));
                return false;
            }

            if (this.formData.tel && !TEL_RE.test(this.formData.tel)) {
                this.toast(this.$t("tMsg.errorTel"));
                return false;
            }

            return true;
        },
        buildPayload() {
            const payload = {};
            const fieldKeys = ["first_name", "last_name", "post_code"];

            fieldKeys.forEach((key) => {
                const currentValue = normalizeText(this.formData[key]);
                const originalValue = normalizeText(this.originalFormData[key]);

                if (!currentValue || currentValue === originalValue) return;
                payload[key] = currentValue;
            });

            const currentTel = normalizeText(this.formData.tel);
            const originalTel = normalizeText(this.originalFormData.tel);

            if (!this.emailReadonly) {
                const currentEmail = normalizeText(this.formData.email);
                const originalEmail = normalizeText(this.originalFormData.email);
                if (currentEmail && currentEmail !== originalEmail) {
                    payload.email = currentEmail;
                }
            }

            if (!this.telReadonly && currentTel && currentTel !== originalTel) {
                payload.tel = currentTel;
            }

            return payload;
        },
        async submitProfile() {
            if (!this.hasChanges) return;
            if (!this.validateForm()) return;

            const payload = this.buildPayload();
            if (!Object.keys(payload).length) {
                this.toast(this.$t("m.profileNoChanges"));
                return;
            }

            this.showLoading();
            try {
                const res = await editProfile(payload);
                this.toast(res.message);
                await this.loadProfile();
            } catch (error) {
                this.toast(error && error.message);
            } finally {
                this.hideLoading();
            }
        },
        async confirmDeleteAccount() {
            this.showLoading();
            try {
                const res = await deleteAccount();
                this.isDeleteConfirmVisible = false;
                this.toast(res.message);
                this.$store.dispatch("setToken", "");
                this.$store.dispatch("setIsBorrow", undefined);
                this.$store.dispatch("setOrderInfo", {});
                this.$router.go(-1);
            } catch (error) {
                this.toast(error && error.message);
            } finally {
                this.hideLoading();
            }
        },
    },
};
</script>

<style lang="scss">
.profile-page {
    min-height: 100vh;
    background: #f6f6f6;
}

.profile-page-body {
    min-height: calc(100vh - 44px);
    padding: 20px 20px 36px;
    display: flex;
    flex-direction: column;
}

.profile-content-card {
    width: 100%;
    padding: 16px 20px 35px;
    border-radius: 5px;
    background: #fff;
}

.profile-form {
    width: 100%;
}

.profile-field {
    margin: 0 0 10px;
}

.profile-field-label {
    margin-bottom: 6px;
    color: #555;
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
}

.profile-save-btn {
    margin-top: 40px !important;
}

.profile-delete-btn {
    margin: auto auto 0;
    border: 0;
    background: transparent;
    color: #e14b4b;
    font-family: SF Pro Text;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    padding: 24px 0 0;
}

.profile-delete-confirm {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.profile-delete-confirm-content {
    color: #555;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
}

.profile-delete-confirm-btn {
    width: 100%;
    margin-top: 20px !important;
    background: #e14b4b;
}

.r-i-input {
    padding: 0 15px;
    border: 1px solid #e6e6e6;
    width: 100%;
    outline: none;
    border-radius: 5px;
    color: #333;
    line-height: 46px;
    height: 46px;
    margin: 5px 0;
    font-size: 14px;

    &::placeholder {
        color: #c6c6c6;
        font-family: SF Pro Text;
        font-size: 14px;
    }

    &.readonly {
        background: #f7f7f7;
        color: #8e8e8e;
    }
}

.r-i-name-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 10px;
}

input:-webkit-autofill {
    font-family: "SF Pro Text";
    font-weight: 400;
    box-shadow: 0 0 0 0 #fff inset !important;
    -webkit-box-shadow: 0 0 0 30px #fff inset !important;
    -webkit-text-fill-color: #333333 !important;
}
</style>
